import { useEffect, useState } from "react";

import { Button, Callout, Colors, FormGroup } from "@blueprintjs/core";

import { isInWorkspace } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { AttendeeList } from "@/components/pieces/coaching/meetings/MeetingAttendees/components/AttendeeList";
import { InvitedBoxStyle } from "@/components/pieces/coaching/meetings/MeetingAttendees/components/AttendeeManageSection/ManageAttendeesDialog";
import { OptionSelect } from "@/components/pieces/form/OptionSelect";
import { SchedulingForm } from "@/components/pieces/form/SchedulingForm";
import { Card } from "@/components/pieces/roleplays/Card";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import {
  InvitationStatus,
  useAddMeetingAttendeesMutation,
  useCancelAttendeesScheduleMutation,
  useUpdateAttendeesScheduleMutation,
  useWorkspaceMembersQuery,
  WorkspaceMembersQuery,
} from "@/graphql";
import { Attendee, FullMeeting } from "@/types/meetings";
import { WorkspaceScheduleJob } from "@/types/workspaces";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

type Member = NonNullableArrayItem<WorkspaceMembersQuery["workspaceMembers"]>;

interface InviteAttendeesDialogProps {
  meeting: FullMeeting;
  open: boolean;
  afterAddOrUpdateSuccess: () => void;
  onClose: () => void;
  attendees: Attendee[];
  scheduleJob?: WorkspaceScheduleJob;
  hideSchedule?: boolean;
}

export function InviteAttendeesDialog({
  meeting,
  open,
  afterAddOrUpdateSuccess,
  onClose,
  attendees = [],
  scheduleJob,
  hideSchedule = false,
}: InviteAttendeesDialogProps) {
  const [result, setResult] = useState<{
    success: boolean;
    errorMsg?: string;
  } | null>(null);

  const [selectedUsers, setSelectedUsers] = useState<Attendee[]>([]);
  const [selectedUserError, setSelectedUserError] = useState<React.ReactNode>();
  const [isSchedule, setIsSchedule] = useState(false);
  const [scheduleTime, setScheduleTime] = useState<string | null>(null);
  const [haveChange, setHaveChange] = useState(false);

  useEffect(() => {
    if (scheduleJob) {
      setIsSchedule(scheduleJob.status === "Scheduled");
      setScheduleTime(scheduleJob.scheduledAt);

      const attendeesPendingByJob = attendees.filter(
        (item) =>
          item?.scheduledJobId?.toString() === scheduleJob?.id?.toString(),
      );

      setSelectedUsers(attendeesPendingByJob);
    } else {
      setSelectedUsers([]);
      setIsSchedule(false);
      setScheduleTime(null);
    }
  }, [scheduleJob, attendees]);

  const emails = selectedUsers.map((item) => item?.email);

  const onRemoveInvitedAttendee = (attendee: Attendee) => {
    setHaveChange(true);
    setSelectedUsers(
      selectedUsers.filter(
        (item) => item?.user?.email !== attendee?.user?.email,
      ),
    );
  };

  const { data: workspaceMembersData, loading: fetchLoading } =
    useWorkspaceMembersQuery({
      fetchPolicy: "cache-and-network",
      variables: {},
      skip: !isInWorkspace(),
    });

  const [addAttendees, { loading }] = useAddMeetingAttendeesMutation({
    onCompleted: (data) => {
      if (data?.addMeetingAttendees?.ok) {
        setResult({ success: true });
        afterAddOrUpdateSuccess();
      } else {
        setResult({
          success: false,
          errorMsg: "Cannot add attendees. Please contact us.",
        });
      }
    },
    onError: (error) => {
      setResult({
        success: false,
        errorMsg: error?.message,
      });
    },
  });

  const [updateAttendeesSchedule, { loading: updating }] =
    useUpdateAttendeesScheduleMutation({
      onCompleted: (data) => {
        if (data?.updateAttendeesSchedule?.ok) {
          setResult({ success: true });
          afterAddOrUpdateSuccess();
        } else {
          setResult({
            success: false,
            errorMsg: "Cannot update attendees schedule. Please contact us.",
          });
        }
      },
      onError: (error) => {
        setResult({
          success: false,
          errorMsg: error?.message,
        });
      },
    });

  const [cancelAttendeesSchedule, { loading: cancelling }] =
    useCancelAttendeesScheduleMutation({
      onCompleted: (data) => {
        if (data?.cancelAttendeesSchedule?.ok) {
          setResult({ success: true });
          afterAddOrUpdateSuccess();
        } else {
          setResult({
            success: false,
            errorMsg: "Cannot cancel attendees schedule. Please contact us.",
          });
        }
      },
      onError: (error) => {
        setResult({
          success: false,
          errorMsg: error?.message,
        });
      },
    });

  const handleAddAttendees = () => {
    addAttendees({
      variables: {
        meetingId: meeting?.id,
        emails: emails,
        scheduleTime: isSchedule ? scheduleTime : null,
      },
    });
  };

  const handleUpdateAttendeesSchedule = () => {
    updateAttendeesSchedule({
      variables: {
        scheduledJobId: scheduleJob?.id ?? "",
        emails,
        meetingId: meeting?.id,
        scheduleTime: isSchedule ? scheduleTime : null,
      },
    });
  };

  const handleCancelAttendeesSchedule = () => {
    cancelAttendeesSchedule({
      variables: {
        scheduledJobId: scheduleJob?.id ?? "",
        meetingId: meeting?.id,
      },
    });
  };

  const workspaceMembers = ensureNonNullArray(
    workspaceMembersData?.workspaceMembers,
  );

  /**
   * Support checking app to reduce complexity when computing the options
   */
  const excludeAddingEmailCandidatesCheckMap = {
    // organizer is default checked
    ...(meeting?.customer?.email ? { [meeting?.customer?.email]: true } : {}),

    // already invited emails
    ...attendees.reduce<Record<string, boolean>>((acc, item) => {
      if (item?.email && item?.status !== InvitationStatus.Failed) {
        acc[item?.email] = true;
      }
      return acc;
    }, {}),

    // current selected emails
    ...selectedUsers.reduce<Record<string, boolean>>((acc, item) => {
      if (item?.user?.email) {
        acc[item?.user?.email] = true;
      }
      return acc;
    }, {}),
  };

  const options = workspaceMembers.filter(
    (workspaceMember) =>
      !excludeAddingEmailCandidatesCheckMap[workspaceMember?.user?.email],
  );

  const disableInvite = selectedUsers.length === 0 || loading || fetchLoading;
  const disableUpdate =
    selectedUsers.length === 0 || updating || cancelling || fetchLoading;

  const handleSelectUser = (suggestion: Member) => {
    setHaveChange(true);
    const selectedEmail = suggestion?.user?.email;
    const isExisted =
      selectedUsers.findIndex((item) => item?.user?.email === selectedEmail) >
      -1;

    const isExistedMember =
      workspaceMembers.findIndex(
        (item) =>
          item?.user?.email === suggestion?.user?.email &&
          options.findIndex((op) => op?.user?.email === selectedEmail) === -1,
      ) > -1;

    if (!isExisted && !isExistedMember) {
      const attendee: Attendee = {
        ...suggestion.user,
        __typename: "MeetingAttendeeType",
      };

      setSelectedUsers([...selectedUsers, attendee]);
      setSelectedUserError(null);
    } else {
      setSelectedUserError(
        <Box color={Colors.ORANGE2} mt={2}>
          Email already exists or the user has been invited.
        </Box>,
      );
    }
  };

  const buttonText = isSchedule ? "Schedule Invitations" : "Invite Attendees";
  const title = scheduleJob ? "Edit Scheduled Invitation" : "Invite Attendees";

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title={<Box fontSize={18}>{title}</Box>}
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setResult(null);
        setHaveChange(false);
      }}
    >
      <Box p={3}>
        <FormGroup style={{ marginBottom: 16 }} label={<b>Select people</b>}>
          <OptionSelect<Member>
            options={options}
            onSelect={handleSelectUser}
            placeholder="Search name or enter email"
            filterOption={(option, inputValue) =>
              (option.user.name || option.user.email)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
            getOptionLabel={(option) => option.user.name || option.user.email}
          />
          {selectedUserError && selectedUserError}
          {selectedUsers && selectedUsers.length > 0 && (
            <Card style={InvitedBoxStyle}>
              <AttendeeList
                attendees={selectedUsers}
                onClick={onRemoveInvitedAttendee}
                mode="edit"
              />
            </Card>
          )}
        </FormGroup>
        {!hideSchedule && (
          <SchedulingForm
            description="Schedule time to send invitations and grant credits"
            isSchedule={isSchedule}
            scheduleTime={scheduleTime}
            onChangeScheduleTime={(value) => {
              setScheduleTime(value);
              setHaveChange(true);
            }}
            onToggleSchedule={(value) => {
              setIsSchedule(value);
              setHaveChange(true);
            }}
          />
        )}
        {options.length === 0 && selectedUsers.length === 0 && (
          <Callout icon={null} intent="warning">
            All workspace members are currently invited to this session
          </Callout>
        )}
        {result && !result?.success && (
          <Callout icon={null} intent="danger">
            {result?.errorMsg}
          </Callout>
        )}
        <Flex mt={3} justifyContent="space-between">
          {!scheduleJob && (
            <Button
              large={true}
              loading={loading}
              intent="primary"
              disabled={disableInvite}
              onClick={() => handleAddAttendees()}
            >
              {buttonText}
            </Button>
          )}
          {scheduleJob && (
            <>
              <Button
                large={true}
                loading={updating}
                intent="primary"
                disabled={disableUpdate || !haveChange}
                onClick={handleUpdateAttendeesSchedule}
              >
                Save
              </Button>
              <Button
                large={true}
                loading={cancelling}
                intent="danger"
                disabled={disableUpdate}
                onClick={handleCancelAttendeesSchedule}
              >
                Cancel Invitation
              </Button>
            </>
          )}
        </Flex>
      </Box>
    </StyledDialog>
  );
}
